import { Component, ElementRef, NgZone } from '@angular/core';
import { HeroMotifBase, RGB } from '../hero-motif/hero-motif.base';

// One limb of a branch: it grows from its parent's end (the branch's first limb from its root, off
// the stage) at an angle to it, and sways about that joint, the more the further out it is.
export interface Limb {
  parent: number;
  /** Its two kinks: sideways offsets at a third and two thirds of its length, as fractions of it. */
  kinks?: number[];
  /** A small side twig at a tip (a little spray of its own). */
  twig?: boolean;
  /** A branch's root, in page pixels (its first limb only). */
  rx?: number;
  ry?: number;
  angle: number;
  len: number;
  width: number;
  depth: number;
  phase: number;
}

// A branch to grow: its root and first limb, how many forks deep it goes, and the disc round its root
// it is fitted into; the limbs and sprays it got.
export interface Branch {
  x: number;
  y: number;
  angle: number;
  len: number;
  width: number;
  depth: number;
  /** How wide its forks open (1 = Sakura's tight fan). */
  spread?: number;
  /** How much each fork bends down (a branch heavy with blossom droops). */
  droop: number;
  /** The disc it is fitted into, round its root (page pixels). */
  radius: number;
  /** The highest it may reach, before the leaves' and flowers' own room (page pixels from the top). */
  top?: number;
  first?: number;
  last?: number;
  firstSpray?: number;
  lastSpray?: number;
}

// A spray of blossoms on a limb, `at` of the way along it; the flowers are offsets round that point.
export interface Spray {
  limb: number;
  at: number;
  /** `bend`: how a leaf's stalk curves (Autumn), as a share of its length. */
  flowers: { dx: number; dy: number; size: number; tint: number; turn: number; bend?: number }[];
  leaves: { dx: number; dy: number; size: number; turn: number }[];
}

// A petal on the wind: it turns in the plane and flips over (its width runs through a cosine).
export interface Petal {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  spin: number;
  turn: number;
  flip: number;
  flipRate: number;
  tint: number;
  age: number;
  sway: number;
  /** A kick it got (Autumn: thrown up off the mound), in px/s, dying away. */
  kx?: number;
  ky?: number;
}

const LOOP = 40;
const W0 = Math.PI * 2 / LOOP;

/**
 * Sakura's hero board: the tip of a cherry branch reaching in from the left, swaying in a soft wind, and
 * its petals drifting across the page. The pointer over the stage (or a finger, or the counter
 * counting) brings a gust: the twigs sway wider, the branch sheds more, the petals fly faster and swirl
 * away round the pointer. The colours are the theme's colour circle (compactPalette): the two pinks
 * for the blossoms and petals, the pale green for the young leaves, the deep pink darkened for the
 * bark, and a very soft mesh of pale pink and white under it all. Reduced motion (and the page's wallpaper, `frozen`) draws
 * one still moment.
 */
@Component( {
  selector: 'app-hero-sakura',
  template: '<canvas #canvas class="hs-canvas" aria-hidden="true"></canvas>',
  styles: [ ':host { display: block; position: absolute; inset: 0; overflow: hidden; pointer-events: none; }',
    '.hs-canvas { display: block; position: absolute; inset: 0; }' ],
} )
export class HeroSakuraComponent extends HeroMotifBase {

  protected limbs: Limb[] = [];
  protected sprays: Spray[] = [];
  protected petals: Petal[] = [];
  protected flowerSprites: HTMLCanvasElement[] = [];
  protected petalSprites: HTMLCanvasElement[] = [];
  protected leafSprite: HTMLCanvasElement | null = null;
  /** What drifts and what grows can be told apart by a motif built on this one (Autumn: leaves for both). */
  protected petalSize: [ number, number ] = [ 13, 9.7 ];
  /** The blossoms turn each its own way (a leaf has an up and down; a five-petal flower hardly shows it). */
  protected turnFlowers = false;
  /** How big and how many the blossoms on the branch are (1 = Sakura's). */
  protected flowerScale = 1;
  protected flowerCount = 1;
  /** How strongly the mesh's coloured patches show against its white (1 = Sakura's). */
  protected meshStrength = 1;
  // the limbs' ends this frame (x, y), laid out from the root each frame as the limbs sway
  private ends: Float32Array = new Float32Array( 0 );
  private heads: Float32Array = new Float32Array( 0 );
  protected scaleK = 1;
  private shed = 0;

  constructor( zone: NgZone, host: ElementRef<HTMLElement> ) {
    super( zone, host );
    this.loop = LOOP;
    this.ease = 1.4;
  }

  protected colorsChanged(): void {
    this.flowerSprites = [];
  }

  /** The motion's tempo: the limbs sway slowly at rest, quicker in a gust. */
  protected speed(): number {
    return 0.45 + 0.8 * this.energy;
  }

  // ---- the tree and the petals ----

  /**
   * Grow the branch: a close-up of a branch's end (owner, 2026-10-16: no trunk, only the end of a
   * branch, close, thick with blossom) at the far left, its limbs fanning out from one root at the edge
   * so what shows is a half disc of blossom near the middle of the height, small side twigs at its
   * tips. The disc keeps to the left of the stage, so the logo, the motto and the counter stay clear.
   * A tall board (a phone) gets it near its top.
   */
  protected build( rand: () => number ): void {
    const tall = this.height > this.width * 1.1;
    this.scaleK = Math.max( 0.55, Math.min( 1.25, ( tall ? this.width * 0.9 : this.height ) / 760 ) );
    const W = this.width;
    const H = this.height;
    const branches = this.branches( rand, tall );
    this.limbs = [];
    this.sprays = [];
    for ( const b of branches ) { this.grow( b, rand ); }
    this.ends = new Float32Array( this.limbs.length * 2 );
    this.heads = new Float32Array( this.limbs.length * 2 );
    this.fit( branches );
    // the petals already in the air, spread over the board
    const count = Math.round( W * H / ( tall ? 16000 : 13000 ) );
    this.petals = [];
    for ( let i = 0; i < count; i++ ) {
      this.petals.push( this.petal( rand() * W, rand() * H, rand, true ) );
    }
  }

  /** One branch: its limbs (two twigs at a fork, now and then three, fanning round its line) and its blossom. */
  protected grow( b: Branch, rand: () => number ): void {
    const k = this.scaleK;
    b.first = this.limbs.length;
    this.limbs.push( { parent: -1, angle: b.angle, len: b.len, width: b.width, depth: 0, phase: rand() * 6.28, rx: b.x, ry: b.y } );
    const fork = ( parent: number ) => {
      const p = this.limbs[ parent ];
      if ( p.depth >= b.depth ) { return; }
      const forks = p.depth > 0 && rand() < 0.28 ? 3 : 2;
      for ( let f = 0; f < forks; f++ ) {
        const side = forks === 2 ? ( f === 0 ? -1 : 1 ) : f - 1;
        const spread = ( 0.14 + rand() * 0.2 ) * ( b.spread || 1 );
        const lean = b.droop + 0.02 * p.depth;
        this.limbs.push( {
          parent, angle: side * spread + lean * ( rand() * 0.8 + 0.2 ),
          len: p.len * ( 0.62 + rand() * 0.18 ),
          width: Math.max( 1.6, p.width * ( 0.62 + rand() * 0.1 ) ),
          depth: p.depth + 1, phase: rand() * 6.28,
        } );
        fork( this.limbs.length - 1 );
      }
    };
    fork( b.first );
    // small side twigs at the tips, each with a little spray of its own (owner, 2026-10-16)
    const tips = this.limbs.length;
    for ( let i = b.first + 1; i < tips; i++ ) {
      const p = this.limbs[ i ];
      if ( p.depth < b.depth - 1 ) { continue; }
      const twigs = 1 + Math.floor( rand() * 2 );
      for ( let t = 0; t < twigs; t++ ) {
        this.limbs.push( {
          parent: i, angle: ( rand() < 0.5 ? -1 : 1 ) * ( 0.55 + rand() * 0.4 ),
          len: p.len * ( 0.6 + rand() * 0.3 ), width: Math.max( 1, p.width * 0.5 ),
          depth: p.depth + 1, phase: rand() * 6.28, twig: true,
        } );
      }
    }
    b.last = this.limbs.length;
    // a cherry limb grows in short zigzags from bud to bud: two kinks along each, never a ruler line
    for ( let i = b.first; i < b.last; i++ ) {
      this.limbs[ i ].kinks = [ ( rand() - 0.5 ) * 0.18, ( rand() - 0.5 ) * 0.18 ];
    }
    b.firstSpray = this.sprays.length;
    this.dress( b, rand );
    b.lastSpray = this.sprays.length;
  }

  /** The branches to grow (Sakura: the half disc's fan). */
  protected branches( rand: () => number, tall: boolean ): Branch[] {
    const k = this.scaleK;
    const W = this.width;
    const H = this.height;
    // what shows is a half disc of blossom bulging in from the left edge (owner, 2026-10-16: "tam bir
    // yarım daire"): limbs fan out from one root at the edge, each fitted inside the disc's radius
    const radius = tall ? Math.min( W * 0.4, H * 0.135 ) : Math.min( W * 0.18, H * 0.32 );
    const root = { x: -radius * 0.08, y: tall ? H * 0.16 : H * 0.36 };
    const fan = tall ? 5 : 6;
    const branches: Branch[] = [];
    for ( let f = 0; f < fan; f++ ) {
      // the fan turned a little up, and reaching down far enough to fill the bottom (owner, 2026-10-16);
      // the hub sits off the stage, so what shows are limbs, not spokes from a point
      const angle = -1.62 + 2.95 * ( f + 0.5 ) / fan + ( rand() - 0.5 ) * 0.14;
      branches.push( { ...root, angle, len: radius * 0.55, width: ( 15 + rand() * 6 ) * k, depth: 3, droop: 0, radius: radius * 1.12, top: H * 0.03 } );
    }
    return branches;
  }

  /** The blossom on a branch's limbs (Sakura: sprays of flowers, more toward the tips). */
  protected dress( b: Branch, rand: () => number ): void {
    const k = this.scaleK;
    // thick with blossom, so the wood hardly shows (owner: "dal daha da az görünsün")
    for ( let i = b.first; i < b.last; i++ ) {
      const l = this.limbs[ i ];
      const count = l.twig ? 1 : 2;
      for ( let c = 0; c < count; c++ ) {
        const flowers: Spray[ 'flowers' ] = [];
        // the limbs reaching down carry a little less blossom (owner, 2026-10-16)
        const low = b.angle > 0.35 ? 0.65 : 1;
        const n = Math.max( 1, Math.round( ( l.twig ? 2 + Math.floor( rand() * 3 ) : 7 + Math.floor( rand() * 4 ) ) * low * this.flowerCount ) );
        const reach = ( l.twig ? 6 + 8 * rand() : 24 + 26 * rand() ) * k;
        for ( let f = 0; f < n; f++ ) {
          const a = rand() * Math.PI * 2;
          const d = Math.sqrt( rand() ) * reach;
          flowers.push( {
            dx: Math.cos( a ) * d, dy: Math.sin( a ) * d * 0.8, size: ( l.twig ? 20 + rand() * 12 : 32 + rand() * 24 ) * k * this.flowerScale,
            tint: Math.floor( rand() * 3 ), turn: rand() * Math.PI * 2,
          } );
        }
        const leaves: Spray[ 'leaves' ] = [];
        if ( rand() < 0.45 ) {
          leaves.push( { dx: ( rand() - 0.5 ) * reach * 2, dy: ( rand() - 0.5 ) * reach, size: ( 22 + rand() * 12 ) * k, turn: rand() * Math.PI * 2 } );
        }
        this.sprays.push( { limb: i, at: c === 0 ? 1 : 0.25 + rand() * 0.6, flowers, leaves } );
      }
    }
  }

  /**
   * Shrink each branch until it keeps inside its disc, measured from its root, so the hero's words
   * stay clear.
   */
  protected fit( branches: Branch[] ): void {
    const saved = this.energy;
    this.energy = 0;
    this.pose();
    this.energy = saved;
    const pad = 40 * this.scaleK;
    for ( const b of branches ) {
      // the side twigs at the tips may reach a little past the disc: the branch is fitted without them
      let reach = 0;
      let minY = b.y;
      for ( let i = b.first!; i < b.last!; i++ ) {
        if ( this.limbs[ i ].twig ) { continue; }
        reach = Math.max( reach, Math.hypot( this.ends[ i * 2 ] - b.x, this.ends[ i * 2 + 1 ] - b.y ) );
        minY = Math.min( minY, this.ends[ i * 2 + 1 ] );
      }
      // and it never reaches over the stage's top, whatever the width (owner, 2026-10-16: the tree was
      // cut off on a wide screen; it keeps its size and place)
      const top = ( b.top ?? 0 ) + pad;
      const f = Math.min( 1, Math.max( 0.05, ( b.radius - pad ) / Math.max( 1, reach ) ),
        minY < top ? Math.max( 0.05, ( b.y - top ) / Math.max( 1, b.y - minY ) ) : 1 );
      if ( f >= 1 ) { continue; }
      for ( let i = b.first!; i < b.last!; i++ ) {
        const l = this.limbs[ i ];
        l.len *= f;
        l.width = Math.max( 1, l.width * ( 0.4 + 0.6 * f ) );
      }
      const g = 0.55 + 0.45 * f;
      for ( let i = b.firstSpray!; i < b.lastSpray!; i++ ) {
        const sp = this.sprays[ i ];
        for ( const fl of sp.flowers ) { fl.dx *= g; fl.dy *= g; fl.size *= g; }
        for ( const lf of sp.leaves ) { lf.dx *= g; lf.dy *= g; lf.size *= g; }
      }
    }
  }

  protected petal( x: number, y: number, rand: () => number = Math.random, grown = false ): Petal {
    const k = this.scaleK;
    return {
      x, y, vx: 10 + rand() * 20, vy: 16 + rand() * 22,
      size: ( this.petalSize[ 0 ] + rand() * this.petalSize[ 1 ] ) * k, spin: ( rand() - 0.5 ) * 1.6, turn: rand() * Math.PI * 2,
      flip: rand() * Math.PI * 2, flipRate: 1.2 + rand() * 2.2, tint: this.petalTint( rand() ),
      age: grown ? 10 : 0, sway: rand() * Math.PI * 2,
    };
  }

  /** The tree laid out this frame: each limb from its parent's end, at its angle plus its sway. */
  private pose(): void {
    const e = this.energy;
    const t = this.time;
    const abs = new Float32Array( this.limbs.length );
    this.limbs.forEach( ( l, i ) => {
      // a slow sway at every joint, the outer ones wider, and a quicker flutter on the twigs; the
      // gust both widens them and leans the whole crown with the wind
      const amp = ( 0.01 + 0.012 * l.depth ) * ( 1 + 1.8 * e );
      const sway = amp * Math.sin( t * W0 * 4 + l.phase + l.depth * 0.5 )
        + ( l.depth >= 3 ? 0.012 * ( 1 + 2 * e ) * Math.sin( t * W0 * 13 + l.phase * 3 ) : 0 )
        + 0.012 * l.depth * e;
      const baseAngle = l.parent < 0 ? 0 : abs[ l.parent ];
      const a = baseAngle + l.angle + sway;
      abs[ i ] = a;
      const hx = l.parent < 0 ? l.rx! : this.ends[ l.parent * 2 ];
      const hy = l.parent < 0 ? l.ry! : this.ends[ l.parent * 2 + 1 ];
      this.heads[ i * 2 ] = hx;
      this.heads[ i * 2 + 1 ] = hy;
      this.ends[ i * 2 ] = hx + Math.cos( a ) * l.len;
      this.ends[ i * 2 + 1 ] = hy + Math.sin( a ) * l.len;
    } );
  }

  private sprayAt( s: Spray ): [ number, number ] {
    const i = s.limb * 2;
    return [ this.heads[ i ] + ( this.ends[ i ] - this.heads[ i ] ) * s.at, this.heads[ i + 1 ] + ( this.ends[ i + 1 ] - this.heads[ i + 1 ] ) * s.at ];
  }

  /** The petals move on the wind: a soft breeze that rises and falls, a gust when awake, a swirl round the pointer. */
  private blow( dt: number ): void {
    const e = this.energy;
    const W = this.width;
    const H = this.height;
    const t = this.time;
    const breeze = 1 + 0.35 * Math.sin( t * W0 * 2 );
    const gust = 1 + 2.6 * e;
    for ( const p of this.petals ) {
      p.age += dt;
      p.sway += dt * 1.4;
      const wx = ( p.vx * breeze + 14 * Math.sin( p.sway ) ) * gust;
      const wy = p.vy * ( 1 + 0.6 * e ) + 8 * Math.cos( p.sway * 0.7 );
      let px = 0;
      let py = 0;
      if ( this.near > 0.01 ) {
        // the pointer stirs the air: petals near it are pushed out and round it
        const dx = p.x - this.pointerX;
        const dy = p.y - this.pointerY;
        const d2 = dx * dx + dy * dy;
        const f = this.near * Math.exp( -d2 / ( 2 * 120 * 120 ) ) * 220;
        const d = Math.sqrt( d2 ) || 1;
        px = ( dx / d - dy / d * 0.8 ) * f;
        py = ( dy / d + dx / d * 0.8 ) * f;
      }
      p.x += ( wx + px + ( p.kx || 0 ) ) * dt;
      p.y += ( wy + py + ( p.ky || 0 ) ) * dt;
      if ( p.kx || p.ky ) {
        const fade = Math.pow( 0.35, dt );
        p.kx = ( p.kx || 0 ) * fade;
        p.ky = ( p.ky || 0 ) * fade;
        if ( Math.abs( p.kx ) + Math.abs( p.ky ) < 2 ) { p.kx = p.ky = 0; }
      }
      p.turn += p.spin * dt * gust;
      p.flip += p.flipRate * dt * ( 1 + e );
      if ( this.land( p ) || p.x > W + 30 || p.y > H + 30 || p.x < -60 || p.y < -60 ) { this.respawn( p ); }
    }
    // the tree sheds a few petals as it sways, many in a gust
    if ( this.frozen ) { return; }
    this.shed += dt * ( 0.6 + 6 * e );
    while ( this.shed >= 1 && this.sprays.length ) {
      this.shed -= 1;
      const p = this.petals[ Math.floor( Math.random() * this.petals.length ) ];
      if ( p && p.age > 2 ) {
        const [ x, y ] = this.sprayAt( this.sprays[ Math.floor( Math.random() * this.sprays.length ) ] );
        Object.assign( p, this.petal( x, y ) );
      }
    }
  }

  /** A petal that left the board comes back in from the top or the left, out of sight. */
  private respawn( p: Petal ): void {
    const fromTop = Math.random() < 0.7;
    Object.assign( p, this.petal( fromTop ? Math.random() * this.width * 0.9 - 40 : -30, fromTop ? -30 : Math.random() * this.height * 0.7 ) );
    p.age = 10;
  }

  // ---- the drawing ----

  protected paint( ctx: CanvasRenderingContext2D, dt: number ): void {
    if ( !this.flowerSprites.length ) { this.makeSprites(); }
    const [ pink, deep ] = this.five();
    const e = this.energy;
    const W = this.width;
    const H = this.height;
    if ( dt > 0 ) { this.blow( dt ); }
    this.pose();

    // the ground: a very soft mesh of pale pink and white (owner, 2026-10-16), its patches drifting
    // slowly; the stage only, the page's wallpaper stays the page's own colour
    if ( !this.frozen ) {
      const [ blush, white ] = this.meshTints( pink );
      const [ c1, c2, c3 ] = this.meshPatches( blush );
      const t = this.time;
      const R = Math.max( W, H );
      const patches: [ number, number, number, RGB, number ][] = [
        // mostly white, the pale pink only in a few soft patches (owner: "beyaz ağırlıklı")
        [ 0.15 + 0.05 * Math.sin( t * W0 * 2 ), 0.2 + 0.04 * Math.cos( t * W0 * 3 ), 0.5, c1, 0.32 * this.meshStrength ],
        [ 0.6 + 0.05 * Math.sin( t * W0 * 1 + 2 ), 0.88 + 0.03 * Math.cos( t * W0 * 2 ), 0.45, c2, 0.24 * this.meshStrength ],
        [ 0.95, 0.6 + 0.05 * Math.sin( t * W0 * 1 + 4 ), 0.36, c3, 0.2 * this.meshStrength ],
        [ 0.75 + 0.04 * Math.cos( t * W0 * 2 ), 0.22 + 0.05 * Math.sin( t * W0 * 1 ), 0.7, white, 0.9 ],
        [ 0.35 + 0.04 * Math.cos( t * W0 * 3 + 1 ), 0.6 + 0.04 * Math.sin( t * W0 * 2 + 1 ), 0.6, white, 0.85 ],
      ];
      for ( const [ px, py, pr, c, a ] of patches ) {
        const g = ctx.createRadialGradient( px * W, py * H, 0, px * W, py * H, pr * R );
        g.addColorStop( 0, this.rgba( c, a ) );
        g.addColorStop( 1, this.rgba( c, 0 ) );
        ctx.fillStyle = g;
        ctx.fillRect( 0, 0, W, H );
      }
    }

    // what lies on the ground (Autumn: the fallen leaves)
    if ( !this.frozen ) { this.paintGround( ctx, dt ); }

    // the petals behind the tree, a third of them
    this.drawPetals( ctx, 0, 3 );

    // the page's wallpaper is the petals alone: a second tree under every box of the page was too much
    if ( this.frozen ) {
      this.drawPetals( ctx, 1, 3 );
      return;
    }

    // the limbs: the bark is the deep pink darkened, lighter on the twigs (farther from us). Each limb
    // tapers along its zigzag, catches a little light along its upper side, and the thicker ones carry
    // the cherry's bark marks, short light bands across the limb
    const bark = this.barkColor( deep );
    const twig = this.mix( bark, pink, 0.3 );
    const shine = this.mix( bark, [ 255, 255, 255 ], 0.38 );
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    const pts: number[] = [ 0, 0, 0, 0, 0, 0, 0, 0 ];
    this.limbs.forEach( ( l, i ) => {
      const hx = this.heads[ i * 2 ], hy = this.heads[ i * 2 + 1 ];
      const ex = this.ends[ i * 2 ], ey = this.ends[ i * 2 + 1 ];
      const dx = ex - hx, dy = ey - hy;
      const len = Math.hypot( dx, dy ) || 1;
      const nx = -dy / len, ny = dx / len;
      const k1 = ( l.kinks?.[ 0 ] || 0 ) * len, k2 = ( l.kinks?.[ 1 ] || 0 ) * len;
      pts[ 0 ] = hx; pts[ 1 ] = hy;
      pts[ 2 ] = hx + dx / 3 + nx * k1; pts[ 3 ] = hy + dy / 3 + ny * k1;
      pts[ 4 ] = hx + dx * 2 / 3 + nx * k2; pts[ 5 ] = hy + dy * 2 / 3 + ny * k2;
      pts[ 6 ] = ex; pts[ 7 ] = ey;
      const colour = this.mix( bark, twig, Math.min( 1, l.depth / 4 ) );
      for ( let sgm = 0; sgm < 3; sgm++ ) {
        const w = l.width * ( 1 - 0.32 * ( sgm + 0.5 ) / 3 );
        const ax = pts[ sgm * 2 ], ay = pts[ sgm * 2 + 1 ], bx = pts[ sgm * 2 + 2 ], by = pts[ sgm * 2 + 3 ];
        // solid wood: no see-through bark where the segments meet (owner, 2026-10-16)
        ctx.strokeStyle = this.rgba( colour, 1 );
        ctx.lineWidth = w;
        ctx.beginPath();
        ctx.moveTo( ax, ay );
        ctx.lineTo( bx, by );
        ctx.stroke();
        if ( w > 3 ) {
          // the light along the upper side
          const sx = bx - ax, sy = by - ay;
          const sl = Math.hypot( sx, sy ) || 1;
          let ux = -sy / sl, uy = sx / sl;
          if ( uy > 0 ) { ux = -ux; uy = -uy; }
          ctx.strokeStyle = this.rgba( this.mix( colour, shine, 0.32 ), 1 );
          ctx.lineWidth = w * 0.22;
          ctx.beginPath();
          ctx.moveTo( ax + ux * w * 0.26, ay + uy * w * 0.26 );
          ctx.lineTo( bx + ux * w * 0.26, by + uy * w * 0.26 );
          ctx.stroke();
          // the bark marks across the limb
          if ( w > 6 ) {
            ctx.strokeStyle = this.rgba( this.mix( colour, shine, 0.28 ), 1 );
            ctx.lineWidth = 1.2;
            const marks = Math.floor( sl / 16 );
            for ( let m = 1; m <= marks; m++ ) {
              const f = m / ( marks + 1 );
              const cx = ax + sx * f, cy = ay + sy * f;
              const half = w * 0.3;
              ctx.beginPath();
              ctx.moveTo( cx - ux * half, cy - uy * half );
              ctx.lineTo( cx + ux * half * 0.6, cy + uy * half * 0.6 );
              ctx.stroke();
            }
          }
        }
      }
    } );

    // the young leaves, then the blossom over them
    if ( this.leafSprite ) {
      ctx.globalAlpha = 0.9;
      for ( const s of this.sprays ) {
        const [ x, y ] = this.sprayAt( s );
        for ( const lf of s.leaves ) { this.sprite( ctx, this.leafSprite, x + lf.dx, y + lf.dy, lf.size, lf.size * 0.55, lf.turn ); }
      }
    }
    ctx.globalAlpha = 1;
    for ( const s of this.sprays ) {
      const [ x, y ] = this.sprayAt( s );
      for ( const f of s.flowers ) { this.drawFlower( ctx, x, y, f ); }
    }

    // the rest of the petals in front
    this.drawPetals( ctx, 1, 3 );

    // a gust lights the blossom a touch, as if the sun came through
    if ( e > 0.02 && !this.lightSurface ) {
      ctx.globalCompositeOperation = 'lighter';
      ctx.globalAlpha = 0.12 * e;
      for ( const s of this.sprays ) {
        const [ x, y ] = this.sprayAt( s );
        ctx.drawImage( this.flowerSprites[ 2 ], x - 18, y - 18, 36, 36 );
      }
    }
  }

  /** Which petal sprite a new petal wears, from a random 0..1: the pale pink two times in three. */
  protected petalTint( r: number ): number {
    return r < 0.65 ? 0 : 1;
  }

  /** A petal reached the ground: true when the motif keeps it there (Sakura lets its petals drift off). */
  protected land( p: Petal ): boolean {
    return false;
  }

  /** Paint what lies on the ground, over the mesh and behind everything else (Sakura: nothing). */
  protected paintGround( ctx: CanvasRenderingContext2D, dt: number ): void {
  }

  /** The three coloured patches of the mesh (Sakura: all the pale pink). */
  protected meshPatches( blush: RGB ): RGB[] {
    return [ blush, blush, blush ];
  }

  /** The bark: Sakura's deep pink darkened. */
  protected barkColor( deep: RGB ): RGB {
    return this.mix( deep, [ 30, 8, 18 ], this.lightSurface ? 0.66 : 0.4 );
  }

  /** One blossom of a spray at (x, y). */
  protected drawFlower( ctx: CanvasRenderingContext2D, x: number, y: number, f: Spray[ 'flowers' ][ number ] ): void {
    const img = this.flowerSprites[ f.tint % this.flowerSprites.length ];
    if ( this.turnFlowers ) {
      this.sprite( ctx, img, x + f.dx, y + f.dy, f.size, f.size, f.turn );
    } else {
      ctx.drawImage( img, x + f.dx - f.size / 2, y + f.dy - f.size / 2, f.size, f.size );
    }
  }

  /** The two colours of the ground's mesh: its soft patches, and the white that weighs most. */
  protected meshTints( first: RGB ): [ RGB, RGB ] {
    const white: RGB = [ 255, 255, 255 ];
    return [ this.mix( first, white, 0.6 ), white ];
  }

  /** The petals whose index falls in `part` of `of` (the layers in front of and behind the tree). */
  private drawPetals( ctx: CanvasRenderingContext2D, part: number, of: number ): void {
    for ( let i = 0; i < this.petals.length; i++ ) {
      const inFront = i % of !== 0;
      if ( ( part === 0 ) === inFront ) { continue; }
      const p = this.petals[ i ];
      // a shed petal fades in as it leaves the spray, never pops up
      ctx.globalAlpha = Math.min( 1, p.age / 0.6 ) * ( this.lightSurface ? 0.92 : 0.85 );
      const w = p.size * ( 0.25 + 0.75 * Math.abs( Math.cos( p.flip ) ) );
      this.sprite( ctx, this.petalSprites[ p.tint ], p.x, p.y, w, p.size, p.turn );
    }
    ctx.globalAlpha = 1;
  }

  /** A sprite at (x, y), `w` by `h`, turned by `turn`. */
  protected sprite( ctx: CanvasRenderingContext2D, img: HTMLCanvasElement, x: number, y: number, w: number, h: number, turn: number ): void {
    const c = Math.cos( turn );
    const s = Math.sin( turn );
    const scale = ctx.getTransform().a;
    ctx.save();
    ctx.setTransform( c * scale, s * scale, -s * scale, c * scale, x * scale, y * scale );
    ctx.drawImage( img, -w / 2, -h / 2, w, h );
    ctx.restore();
  }

  /** The colour circle's five colours, or ones made from the theme's accents when it gives none. */
  protected five(): RGB[] {
    if ( this.paletteColors.length >= 5 ) { return this.paletteColors.slice( 0, 5 ); }
    const [ primary, secondary ] = this.colors;
    return [ this.mix( primary, [ 255, 255, 255 ], 0.5 ), primary, secondary, [ 105, 162, 189 ], [ 96, 127, 169 ] ];
  }

  /**
   * The flowers, petals and leaf, painted once per colour change. A flower is five rounded petals,
   * each with the cherry's notch at its tip, round a deep pink heart; three tints, from the pale pink
   * to a blush of the deep one. A petal on the wind is one of those petals alone.
   */
  protected makeSprites(): void {
    const [ pink, deep, green ] = this.five();
    const white: RGB = [ 255, 255, 255 ];
    const tints: RGB[] = [ pink, this.mix( pink, deep, 0.35 ), this.mix( pink, white, 0.45 ) ];
    const petalPath = ( g: CanvasRenderingContext2D, len: number, wid: number ) => {
      // from the base at the origin to a notched tip at -len
      g.beginPath();
      g.moveTo( 0, 0 );
      g.bezierCurveTo( wid, -len * 0.25, wid * 1.05, -len * 0.85, wid * 0.32, -len );
      g.lineTo( 0, -len * 0.86 );
      g.lineTo( -wid * 0.32, -len );
      g.bezierCurveTo( -wid * 1.05, -len * 0.85, -wid, -len * 0.25, 0, 0 );
      g.closePath();
    };
    this.flowerSprites = tints.map( tint => {
      const c = document.createElement( 'canvas' );
      c.width = c.height = 128;
      const g = c.getContext( '2d' )!;
      g.translate( 64, 64 );
      g.scale( 2, 2 );
      for ( let i = 0; i < 5; i++ ) {
        g.save();
        g.rotate( i * Math.PI * 2 / 5 );
        const grad = g.createLinearGradient( 0, 0, 0, -30 );
        grad.addColorStop( 0, this.rgba( this.mix( tint, deep, 0.45 ), 1 ) );
        grad.addColorStop( 0.35, this.rgba( tint, 1 ) );
        grad.addColorStop( 1, this.rgba( this.mix( tint, white, 0.3 ), 1 ) );
        g.fillStyle = grad;
        petalPath( g, 29, 15 );
        g.fill();
        g.restore();
      }
      g.fillStyle = this.rgba( deep, 0.9 );
      g.beginPath();
      g.arc( 0, 0, 4.5, 0, Math.PI * 2 );
      g.fill();
      return c;
    } );
    this.petalSprites = [ tints[ 0 ], tints[ 1 ] ].map( tint => {
      const c = document.createElement( 'canvas' );
      c.width = 32;
      c.height = 48;
      const g = c.getContext( '2d' )!;
      g.translate( 16, 46 );
      const grad = g.createLinearGradient( 0, 0, 0, -44 );
      grad.addColorStop( 0, this.rgba( this.mix( tint, deep, 0.35 ), 1 ) );
      grad.addColorStop( 0.4, this.rgba( tint, 1 ) );
      grad.addColorStop( 1, this.rgba( this.mix( tint, white, 0.35 ), 1 ) );
      g.fillStyle = grad;
      petalPath( g, 44, 15 );
      g.fill();
      return c;
    } );
    const leaf = document.createElement( 'canvas' );
    leaf.width = 48;
    leaf.height = 26;
    const lg = leaf.getContext( '2d' )!;
    lg.fillStyle = this.rgba( this.mix( green, [ 60, 110, 60 ], 0.25 ), 1 );
    lg.beginPath();
    lg.moveTo( 2, 13 );
    lg.quadraticCurveTo( 24, -4, 46, 13 );
    lg.quadraticCurveTo( 24, 30, 2, 13 );
    lg.fill();
    this.leafSprite = leaf;
  }
}
